import { useEffect, useRef, useState } from "react";

/**
 * Tracks scroll position + a smoothed velocity (delta per frame).
 * Velocity decays toward 0 after the user stops scrolling.
 *
 * Returns { y, velocity } where velocity is a signed number (positive = scrolling down).
 */
export default function useScrollMotion() {
  const [state, setState] = useState({ y: 0, velocity: 0 });
  const lastY = useRef(0);
  const velRef = useRef(0);
  const rafRef = useRef(0);
  const decayRafRef = useRef(0);
  const lastEmitRef = useRef({ y: 0, velocity: 0 });

  useEffect(() => {
    const isMobile = typeof window !== "undefined" && window.matchMedia && window.matchMedia("(max-width: 767px)").matches;
    let ticking = false;

    const emit = (y, velocity) => {
      const last = lastEmitRef.current;
      // Skip React re-render unless meaningful change (prevents per-frame churn)
      if (Math.abs(y - last.y) < 1 && Math.abs(velocity - last.velocity) < 0.25) return;
      lastEmitRef.current = { y, velocity };
      setState({ y, velocity });
    };

    const tick = () => {
      const y = window.scrollY || window.pageYOffset || 0;
      const dy = y - lastY.current;
      lastY.current = y;
      velRef.current = velRef.current * 0.6 + dy * 0.4;
      emit(y, velRef.current);
      ticking = false;
    };

    const onScroll = () => {
      if (!ticking) {
        rafRef.current = window.requestAnimationFrame(tick);
        ticking = true;
      }
    };

    // Continuous decay so velocity returns to 0 when scrolling stops.
    // On mobile we skip the constant decay loop entirely — it's the biggest
    // source of jank during touch scrolling.
    const decay = () => {
      if (Math.abs(velRef.current) > 0.05) {
        velRef.current *= 0.88;
        emit(lastY.current, velRef.current);
      } else if (velRef.current !== 0) {
        velRef.current = 0;
        emit(lastY.current, 0);
      }
      decayRafRef.current = window.requestAnimationFrame(decay);
    };

    window.addEventListener("scroll", onScroll, { passive: true });
    if (!isMobile) {
      decayRafRef.current = window.requestAnimationFrame(decay);
    }
    onScroll();

    return () => {
      window.removeEventListener("scroll", onScroll);
      cancelAnimationFrame(rafRef.current);
      cancelAnimationFrame(decayRafRef.current);
    };
  }, []);

  return state;
}
